import { HitsiTunnus } from "./HitsiTunnus";
import { useState } from "react";
import { api, ApiError, KORJAUKSEN_SYYT, type Hitsi, type KorjauksenSyyLaji } from "./api";
import { t, virheTeksti } from "./kieli";
import type { KaannosAvain } from "./kaannokset";

/* ═══════════════════════════════════════════════════════════
   Hitsin korjaus (T309, Toni 14.9.2026).

   Syy valitaan listalta eikä kirjoiteta: kuudelta aamulla bunkkerissa vapaa
   tekstikenttä on kitkaa, ja kitka johtaa kirjaamatta jättämiseen. Tulos on
   koodattu arvo. «Muu» pyytää lyhyen tekstin. Palvelin päättää, tarvitaanko
   syy (vain kun virallisen lokin kenttä muuttuu) ja saako käyttäjä korjata.
   ═══════════════════════════════════════════════════════════ */

export function KorjauksenSyyValinta({ laji, teksti, asetaLaji, asetaTeksti }: {
  laji: KorjauksenSyyLaji | "";
  teksti: string;
  asetaLaji: (l: KorjauksenSyyLaji | "") => void;
  asetaTeksti: (t: string) => void;
}) {
  return (
    <>
      <label>
        {t("muu.hkor.syy")}
        <select value={laji} onChange={(e) => asetaLaji(e.target.value as KorjauksenSyyLaji | "")}>
          <option value="">{t("hl.valitse_viivat")}</option>
          {KORJAUKSEN_SYYT.map((s) => <option key={s.arvo} value={s.arvo}>{t(`hkor.syy.${s.arvo}` as KaannosAvain)}</option>)}
        </select>
      </label>
      {laji === "muu" && (
        <label className="kasvaa">
          {t("muu.hkor.mika")}
          <input value={teksti} maxLength={500} autoComplete="off" onChange={(e) => asetaTeksti(e.target.value)} />
        </label>
      )}
    </>
  );
}

/** Syy reitin muotoon, tai undefined kun sitä ei ole valittu. */
export function korjauksenSyy(laji: KorjauksenSyyLaji | "", teksti: string) {
  if (!laji) return undefined;
  return { laji, ...(teksti.trim() && { teksti: teksti.trim() }) };
}

/**
 * Kentän korjauslomake: numero, sulatukset, hitsaaja ja asentaja — ne jotka kentällä menevät väärin.
 * Muut kentät korjataan Selailussa.
 */
export function HitsinKorjausKentalla({ h, valmis, peruuta }: {
  h: Hitsi; valmis: () => void; peruuta: () => void;
}) {
  const osa = (j: number) => h.osat.find((o) => o.jarjestys === j);
  const [numero, setNumero] = useState(h.hitsinumero);
  const [s1, setS1] = useState(osa(1)?.sulatus ?? "");
  const [s2, setS2] = useState(osa(2)?.sulatus ?? "");
  const [juuri, setJuuri] = useState(h.juuri_hitsaaja_koodi ?? "");
  const [asentaja, setAsentaja] = useState(h.asentaja_koodi ?? h.tayton_hitsaaja_koodi ?? "");
  const [laji, setLaji] = useState<KorjauksenSyyLaji | "">("");
  const [teksti, setTeksti] = useState("");
  const [tallennetaan, setTallennetaan] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);

  async function tallenna() {
    setTallennetaan(true); setVirhe(null);
    try {
      const syy = korjauksenSyy(laji, teksti);
      await api.muokkaaHitsi(h.id, {
        /* ⛔ Tyhjaa numeroa EI laheteta: reitin schema on `minLength: 1`,
           joten tyhja kaataisi koko korjauksen 400:aan. Pekilossa on 12
           tuontirivia joiden numero on tyhja, ja juuri ne ovat niita joita
           pitaa paasta korjaamaan. `coalesce($3, hitsinumero)` jattaa
           numeron ennalleen kun kentta puuttuu — ja jos kayttaja KIRJOITTAA
           numeron, se menee perille. */
        ...(numero.trim() ? { hitsinumero: numero.trim() } : {}),
        juuri_hitsaaja_koodi: juuri.trim(),
        asentaja_koodi: asentaja.trim(),
        osat: [1, 2].filter((j) => osa(j)).map((j) => ({
          jarjestys: j,
          osalaji: osa(j)?.osalaji ?? undefined,
          materiaali: osa(j)?.materiaali ?? undefined,
          sulatus: (j === 1 ? s1 : s2).trim() || undefined,
        })),
        ...(syy && { korjaus_syy: syy }),
      });
      valmis();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.hkor.virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <section className="panel" data-hitsin-korjaus={h.id}>
      <div className="panel__head">
        <h2>{t("muu.hkor.otsikko")} <HitsiTunnus numero={h.hitsinumero} lehti={h.piirustuslehti} puuttuu={t("muu.hkor.numero_puuttuu")} /></h2>
      </div>
      <div className="allokointi">
        <label>{t("hl.hitsi")}<input value={numero} size={6}
                            placeholder={h.hitsinumero ? "" : t("muu.hkor.ei_numeroa")}
                            onChange={(e) => setNumero(e.target.value)} /></label>
        {osa(1) && <label>{t("muu.hkor.osa_sulatus", { n: 1 })}<input value={s1} size={9} onChange={(e) => setS1(e.target.value)} /></label>}
        {osa(2) && <label>{t("muu.hkor.osa_sulatus", { n: 2 })}<input value={s2} size={9} onChange={(e) => setS2(e.target.value)} /></label>}
        <label>{t("hl.hitsaaja")}<input value={juuri} size={6} onChange={(e) => setJuuri(e.target.value)} /></label>
        <label>{t("hl.asentaja")}<input value={asentaja} size={6} onChange={(e) => setAsentaja(e.target.value)} /></label>
        <KorjauksenSyyValinta laji={laji} teksti={teksti} asetaLaji={setLaji} asetaTeksti={setTeksti} />
        <button className="nappi nappi--paa" disabled={tallennetaan} onClick={() => void tallenna()}>
          {tallennetaan ? t("pu.tallennetaan") : t("muu.hkor.tallenna")}
        </button>
        <button className="nappi nappi--kevyt" disabled={tallennetaan} onClick={peruuta}>{t("hl.peruuta")}</button>
      </div>
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
    </section>
  );
}
